import * as HoverCard from "@radix-ui/react-hover-card";
import type { MedalEntry, MedalItem } from "@/api/types";
import { dateSpanish, fmt } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Two modes:
 *  - List mode (legacy): pass `medals={[...]}` to render the full breakdown
 *    inside a single hover area. Used by RankingRow + PodiumCard.
 *  - Single mode (new): pass `medal={oneItem}` to render a rich tooltip for
 *    one medal slot. Used by MedalTray — shows earned_at on unlock or a
 *    progress bar + current/target on lock.
 */
export interface MedalTooltipProps {
  medals?: MedalEntry[];
  medal?: MedalItem;
  children: React.ReactNode;
}

export function MedalTooltip({ medals, medal, children }: MedalTooltipProps) {
  // Single medal mode — rich progress display.
  if (medal) {
    return (
      <HoverCard.Root openDelay={120} closeDelay={80}>
        <HoverCard.Trigger asChild>
          <span className="cursor-help">{children}</span>
        </HoverCard.Trigger>
        <HoverCard.Portal>
          <HoverCard.Content
            sideOffset={10}
            className="z-50 w-72 rounded-[14px] border border-border-2 bg-bg-1/95 p-4 text-xs text-text-2 shadow-lg backdrop-blur"
          >
            <SingleMedalContent medal={medal} />
            <HoverCard.Arrow className="fill-bg-1" />
          </HoverCard.Content>
        </HoverCard.Portal>
      </HoverCard.Root>
    );
  }

  // Legacy list mode.
  const list = medals ?? [];
  if (list.length === 0) return <>{children}</>;

  return (
    <HoverCard.Root openDelay={120} closeDelay={80}>
      <HoverCard.Trigger asChild>
        <span className="cursor-help">{children}</span>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          sideOffset={8}
          className="z-50 rounded-lg border border-border-2 bg-bg-1/95 p-3 text-xs shadow-lg backdrop-blur"
        >
          <ul className="grid gap-1.5">
            {list.map((m) => (
              <li
                key={m.code}
                className="flex items-center justify-between gap-4"
              >
                <span className="text-text-2">
                  {m.emoji} {m.label}
                </span>
                <span className="tabular font-semibold text-text-1">
                  {m.unlocked ? "✓" : `${m.pct}%`}
                </span>
              </li>
            ))}
          </ul>
          <HoverCard.Arrow className="fill-bg-1" />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}

function SingleMedalContent({ medal }: { medal: MedalItem }) {
  const pct = Math.max(0, Math.min(100, medal.pct));
  const remaining = Math.max(medal.target - medal.current, 0);
  const unit = medal.unit || "";

  if (medal.unlocked) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gold-2/15 text-2xl ring-1 ring-gold-2/40"
          >
            {medal.emoji}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-semibold text-text-1">
              {medal.label}
            </div>
            <div className="mt-0.5 text-[11px] uppercase tracking-wider text-gold-2">
              Conseguida
            </div>
          </div>
        </div>
        {medal.desc && <p className="text-text-3">{medal.desc}</p>}
        {medal.earned_at && (
          <div className="rounded-md border border-border-1 bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-text-3">
            Desbloqueada el{" "}
            <span className="text-text-2">{dateSpanish(medal.earned_at)}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/5 text-2xl opacity-60 grayscale ring-1 ring-border-2"
        >
          {medal.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-text-1">
            {medal.label}
          </div>
          <div className="mt-0.5 text-[11px] uppercase tracking-wider text-text-4">
            Bloqueada
          </div>
        </div>
      </div>

      {medal.desc && <p className="text-text-3">{medal.desc}</p>}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-[11px]">
          <span className="tabular text-text-2">
            {fmt(medal.current)} / {fmt(medal.target)}
            {unit ? ` ${unit}` : ""}
          </span>
          <span className="tabular font-semibold text-brand-1">
            {pct.toFixed(0)}%
          </span>
        </div>
        <div
          className="relative h-1.5 overflow-hidden rounded-full bg-white/5"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={cn(
              "h-full rounded-full bg-gradient-to-r from-brand-1 to-brand-3",
              "motion-reduce:transition-none transition-[width] duration-500",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {remaining > 0 && (
        <div className="rounded-md border border-border-1 bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-text-3">
          Faltan{" "}
          <span className="tabular font-semibold text-text-1">
            {fmt(remaining)}
            {unit ? ` ${unit}` : ""}
          </span>{" "}
          para conseguir la medalla
        </div>
      )}
    </div>
  );
}
